import React, { Component } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { Formik } from 'formik';
import lodashFindIndex from 'lodash/findIndex';
import lodashPick from 'lodash/pick';
import lodashIsEmpty from 'lodash/isEmpty';

import { ID_VERIFICATION_METHOD_MANUAL } from 'common/idVerificationConstants';

import { USER_ROLE_RENTER } from 'common/userRoles';

import { TOUR_REQUEST_SHOWING_FIELDS } from 'common/tourConstants';

import getApplicationFormFields, {
  APPLICATION_FORM_TYPE_RENTER_PROFILE,
  APPLICATION_FORM_TYPE_RENTER_PREQUAL,
  APPLICATION_FORM_TYPE_GUARANTOR_APPLICATION,
} from 'common/householdApplication/applicationFormFields';

import typeCheck from 'common/util/typeCheck';
import objectNormalize from 'common/util/objectNormalize';
import setNullEmptyValues from 'common/util/setNullEmptyValues';
import getLastPageVisitedByScreenIdList from 'common/util/getLastPageVisitedByScreenIdList';
import { shouldSendTourRequestEmail } from 'common/tourHelpers';

import { isListingTenantScreening } from 'common/listingHelpers';

import {
  getHouseholdApplicationDataFields,
  getListingApplicationFormUrl,
} from 'common/householdApplication/applicationHelpers';

import { FIELDS_GROUPS_STEPS } from 'common/householdApplication/rentalApplicationFormFields';

import {
  SR_API_ACTION_HOUSEHOLD_GET_RENTERS,
  SR_API_ACTION_HOUSEHOLD_GET_RENTER_PROFILE,
  SR_API_ACTION_HOUSEHOLD_GET_APPLICATION,
  SR_API_ACTION_HOUSEHOLD_SUBMIT_APPLICATION,
} from 'api/actionTypes';

import { actionResetFeedCategoryList } from 'client/helpers/feedCategoryHelpers';

import {
  createInitialValues,
  createFormValidationFn,
  getRootFieldId,
} from 'client/helpers/formikHelpers';

import {
  SCREEN_ID_APPLICATION_FORM,
  SCREEN_ID_FEED,
  SCREEN_ID_FEED_CATEGORY,
  SCREEN_ID_PREQUAL_FORM_START_PAGE,
  SCREEN_ID_LISTING_CHOOSE_TOUR_TIME,
} from 'client/constants/screenIds';

import branch from 'client/core/branch';
import log from 'client/lib/log';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withApiState from 'client/helpers/withApiState';
import allowedRoles from 'client/helpers/allowedRoles';
import HouseholdActions from 'client/actions/Household';
import HouseholdListingActions from 'client/actions/HouseholdListing';
import setShouldRestoreScrollPosition from 'client/actions/Navigator/setShouldRestoreScrollPosition';
import TourActions from 'client/actions/Tour';
import ListingActions from 'client/actions/Listing';

import SrApplicationFormPage from 'client/components/SrApplicationFormPage';

const debug = log.debug('sunroom:pages:ApplicationForm');
const emptySsn = (obj) => ({ ...obj, ssn: '' });

@withTransitionHook
@allowedRoles({
  roles: [USER_ROLE_RENTER],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@branch({
  listing: ['listing'],
  currentUser: ['currentUser'],
  renterProfilesByUserId: ['household', 'renterProfilesByUserId'],
  householdRentersByUserId: ['household', 'rentersByUserId'],
  householdApplicationsById: ['household', 'applicationsById'],
  screenIdHistory: ['analytics', 'screenIdHistory'],
  pathHistory: ['analytics', 'pathHistory'],
  householdListingsBy: ['household', 'householdListingsBy'],
})
@withApiState(({ match }) => ({
  getRenterProfileApiState: {
    action: SR_API_ACTION_HOUSEHOLD_GET_RENTER_PROFILE,
    queryId: match.params.userId,
  },
  getHouseholdRentersApiState: {
    action: SR_API_ACTION_HOUSEHOLD_GET_RENTERS,
    queryId: match.params.userId,
  },
  getHouseholdApplicationApiState: {
    action: SR_API_ACTION_HOUSEHOLD_GET_APPLICATION,
    queryId: match.params.applicationId,
  },
  submitHouseholdApplicationApiState: {
    action: SR_API_ACTION_HOUSEHOLD_SUBMIT_APPLICATION,
    queryId: match.params.listingId,
  },
}))
@withScrollToTop
@withScreenId(SCREEN_ID_APPLICATION_FORM)
class ApplicationFormPage extends Component {
  static fetchData(match, inject) {
    const locationQuery = queryString.parse(inject.location.search);

    if (match.params.applicationId) {
      return Promise.all([
        HouseholdActions.loadHouseholdApplication(inject, {
          applicationId: parseInt(match.params.applicationId, 10),
        }),
        HouseholdActions.loadHouseholdRenters(inject, {
          userId: parseInt(match.params.userId, 10),
        }),
      ]).then(() => {
        const householdApplication = inject.state.get([
          'household',
          'applicationsById',
          match.params.applicationId,
        ]);
        return HouseholdListingActions.getBy(inject, {
          householdId: householdApplication.householdId,
          listingId: parseInt(match.params.listingId, 10),
        });
      });
    }
    return Promise.all([
      HouseholdActions.loadRenterProfile(inject, {
        userId: parseInt(match.params.userId, 10),
      }),
      HouseholdActions.loadHouseholdRenters(inject, {
        userId: parseInt(match.params.userId, 10),
      }),
      locationQuery.prequalListingId &&
        ListingActions.getById(inject, locationQuery.prequalListingId),
    ]);
  }

  static getPageMetadata = () => ({
    pageTitle: 'Application | Sunroom',
  });

  constructor(props) {
    super(props);
    this.state = {
      applicationFormFields: null,
    };
  }

  static propTypes = {
    history: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    currentUser: PropTypes.object.isRequired,
    renterProfilesByUserId: PropTypes.object.isRequired,
    householdRentersByUserId: PropTypes.object.isRequired,
    householdApplicationsById: PropTypes.object,
    getRenterProfileApiState: PropTypes.object.isRequired,
    getHouseholdRentersApiState: PropTypes.object.isRequired,
    getHouseholdApplicationApiState: PropTypes.object.isRequired,
    submitHouseholdApplicationApiState: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    screenIdHistory: PropTypes.array.isRequired,
    pathHistory: PropTypes.array.isRequired,
    householdListingsBy: PropTypes.object,
  };

  componentDidMount() {
    const {
      location,
      dispatch,
      history,
      match: { params },
    } = this.props;

    const listingId = parseInt(params.listingId, 10) || null;
    const { editMode, fieldId, formType } = queryString.parse(location.search);

    if (editMode === 'field') {
      return;
    }

    if (!fieldId || !formType) {
      if (listingId && formType === APPLICATION_FORM_TYPE_RENTER_PROFILE) {
        dispatch(HouseholdActions.startApplication, {
          listingId,
        });
      } else {
        history.push(this._getBackLink());
      }
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const currentFieldId = queryString.parse(this.props.location.search).fieldId;
    const nextFieldId = queryString.parse(nextProps.location.search).fieldId;

    if (currentFieldId !== nextFieldId && typeof global.scrollTo === 'function') {
      global.scrollTo(0, 0);
    }
  }

  _onFieldSubmit = ({ formikBag, formFieldId, nextFormField, skip, noRedirect }) => {
    const {
      dispatch,
      history,
      location,
      match: { params },
      screenIdHistory,
      pathHistory,
      currentUser,
      householdApplicationsById,
      renterProfilesByUserId,
      listing,
    } = this.props;

    const userId = parseInt(params.userId, 10);
    const listingId = parseInt(params.listingId, 10) || null;
    const applicationId = params.applicationId ? parseInt(params.applicationId, 10) : null;
    const {
      formType,
      editMode,
      shouldRequestTourPrompt,
      prequalListingId,
      householdId: householdIdStr,
      saveExitRedirect,
      fieldGroup,
    } = queryString.parse(location.search);
    const householdId = householdIdStr ? parseInt(householdIdStr, 10) : null;
    const requestedShowing = lodashPick(
      queryString.parse(location.search),
      TOUR_REQUEST_SHOWING_FIELDS,
    );
    const rootFieldId = getRootFieldId(formFieldId);
    const value = formikBag.values[rootFieldId];

    const householdApplication = applicationId
      ? householdApplicationsById[applicationId]
      : renterProfilesByUserId[userId];

    const updates = objectNormalize({
      [rootFieldId]: value,
    });

    if (formFieldId === 'petCount' && Array.isArray(householdApplication?.pets)) {
      updates.pets = householdApplication.pets.slice(0, updates.petCount);
    }

    if (formFieldId === 'governmentIdPhoto') {
      updates.idVerificationMethod = ID_VERIFICATION_METHOD_MANUAL;
    }

    debug('_onFieldSubmit', {
      nextFormField,
      formFieldId,
      skip,
      updates,
      value,
    });

    let updateHouseholdApplicationPromise = Promise.resolve();

    if (!skip) {
      updateHouseholdApplicationPromise = dispatch(HouseholdActions.updateRenterProfile, {
        userId: userId,
        stepId: formFieldId,
        prequalListingId: prequalListingId ? parseInt(prequalListingId, 10) : null,
        updates: setNullEmptyValues(updates),
      }).then((renterProfile) => {
        if (applicationId) {
          return dispatch(HouseholdActions.updateApplication, {
            applicationId,
            stepId: formFieldId,
            updates,
          });
        }
        return Promise.resolve(renterProfile);
      });
    }

    if (noRedirect) {
      if (formType === APPLICATION_FORM_TYPE_RENTER_PREQUAL) {
        dispatch(actionResetFeedCategoryList);
      }
      return;
    }
    if (editMode === 'field') {
      updateHouseholdApplicationPromise.then(() => {
        history.push(!saveExitRedirect ? this._getBackLink() : saveExitRedirect);
      });
    } else if (nextFormField) {
      if (fieldGroup) {
        if (fieldGroup && FIELDS_GROUPS_STEPS[fieldGroup] && saveExitRedirect) {
          const haveMoreFields = FIELDS_GROUPS_STEPS[fieldGroup].includes(nextFormField.id);

          if (!haveMoreFields) {
            history.push(saveExitRedirect);
            return;
          }
        }
      }
      // Updates a field on their renter profile.
      history.push(
        getListingApplicationFormUrl({
          userId: userId,
          listingId: listingId,
          householdId: householdId,
          shouldRequestTourPrompt: shouldRequestTourPrompt,
          prequalListingId: prequalListingId,
          formFieldId: nextFormField.id,
          formType: formType,
          requestedShowing,
          applicationId,
          saveExitRedirect,
          fieldGroup,
        }),
      );
    } else {
      // Submits their renter profile to create a new listing application.
      updateHouseholdApplicationPromise
        .then((renterProfile) => {
          switch (formType) {
            case APPLICATION_FORM_TYPE_RENTER_PROFILE: {
              if (listingId) {
                dispatch(HouseholdActions.submitHouseholdApplication, {
                  userId: userId,
                  listingId: listingId,
                  householdApplication: renterProfile,
                });
                history.push(`/application/${userId}/${listingId}/pay-form`);
              } else {
                dispatch(HouseholdActions.submitRenterProfile, {
                  userId: userId,
                  householdApplication: renterProfile,
                  sendSlackMessage: true,
                });
                history.push(`/application/renter-profile/${userId}/form-confirm`);
              }
              break;
            }
            case APPLICATION_FORM_TYPE_GUARANTOR_APPLICATION: {
              dispatch(HouseholdActions.submitGuarantorApplication, {
                userId: userId,
                householdId: householdId,
                listingId: listingId,
                guarantorApplication: renterProfile,
              }).then((guarantorApplication) => {
                history.push(
                  `/application/payment?${queryString.stringify({
                    applicationId: guarantorApplication.id,
                    listingId: listingId,
                  })}`,
                );
              });
              break;
            }
            case APPLICATION_FORM_TYPE_RENTER_PREQUAL: {
              dispatch(HouseholdActions.markRenterPrequalAsCompleted, {
                userId: userId,
                listingId: prequalListingId ? parseInt(prequalListingId, 10) : null,
              }).then(() => {
                dispatch(actionResetFeedCategoryList);

                // We have two scenarios:
                // 1. For apply after complete the prequal quiz
                // 2. For request a tour after complete the prequal quiz
                if (prequalListingId) {
                  dispatch(HouseholdActions.getRenterListingQualification, {
                    userId: userId,
                    listingId: parseInt(prequalListingId, 10),
                  }).then(({ isQualified }) => {
                    if (listing && isListingTenantScreening(listing)) {
                      history.push(`/apply/${prequalListingId}?showQualifications=true`);
                      return;
                    }
                    if (isQualified && shouldRequestTourPrompt === 'true') {
                      history.push(`/listing/${prequalListingId}/request-tour`);
                    } else {
                      history.push(`/listing/${prequalListingId}?scrollTo=yourQualifications`);
                    }
                  });
                } else {
                  const locationQuery = queryString.parse(location.search || '');
                  const requestedShowing = lodashPick(locationQuery, TOUR_REQUEST_SHOWING_FIELDS);
                  /* In the case the user leaves the prequal form and then decides to come back and
                   * continue the quiz, we should not request the tour. We should instead guide them
                   * to the explore page. It could be the renter doesn't remember what homes they
                   * were scheduling, the listing could be leased, etc.
                   */
                  if (!lodashIsEmpty(requestedShowing)) {
                    dispatch(ListingActions.getById, requestedShowing.listingId).then((listing) => {
                      if (shouldSendTourRequestEmail({ currentUser, listing })) {
                        dispatch(TourActions.sendTourRequestEmail, {
                          listingId: parseInt(requestedShowing.listingId, 10),
                        });
                        history.push(`/listing/${requestedShowing.listingId}/request-tour-email`);
                        return;
                      }

                      dispatch(TourActions.requestShowingStepSubmit, {
                        listingId: parseInt(requestedShowing.listingId, 10),
                        locationQuery: requestedShowing,
                      });
                    });
                  } else {
                    const lastPageVisited = getLastPageVisitedByScreenIdList({
                      screenIdHistory,
                      pathHistory,
                      screenIdList: [SCREEN_ID_FEED, SCREEN_ID_FEED_CATEGORY],
                    });

                    history.push(lastPageVisited ? lastPageVisited.path : '/feed');
                  }
                }
              });
              break;
            }
          }
        })
        .catch((error) => log.error(error));
    }
  };

  _onFormFieldsReady = (applicationFormFields) => {
    this.setState({
      applicationFormFields,
    });
  };

  _getBackLink = () => {
    const {
      location,
      dispatch,
      match: { params },
      screenIdHistory,
      pathHistory,
    } = this.props;
    const { applicationFormFields } = this.state;

    const userId = parseInt(params.userId, 10) || null;
    const listingId = parseInt(params.listingId, 10) || null;
    const applicationId = parseInt(params.applicationId, 10) || null;

    const {
      editMode,
      fieldId,
      formType,
      prequalListingId,
      householdId: householdIdStr,
      saveExitRedirect,
      fieldGroup,
    } = queryString.parse(location.search);

    const householdId = householdIdStr ? parseInt(householdIdStr, 10) : null;
    const isRenterProfileApplication = location.pathname.includes('renter-profile');
    const requestedShowing = lodashPick(
      queryString.parse(location.search),
      TOUR_REQUEST_SHOWING_FIELDS,
    );

    if (!applicationFormFields || !userId || (!listingId && !isRenterProfileApplication)) {
      return '/';
    }

    const formFieldIndex = lodashFindIndex(applicationFormFields, (formField) => {
      return formField.id.toLowerCase() === fieldId.toLowerCase();
    });
    const previousFormField = applicationFormFields[formFieldIndex - 1];

    if (editMode === 'field') {
      dispatch(setShouldRestoreScrollPosition);
    }

    const lastPageVisited = getLastPageVisitedByScreenIdList({
      screenIdHistory,
      pathHistory,
      screenIdList: [
        SCREEN_ID_FEED,
        SCREEN_ID_FEED_CATEGORY,
        SCREEN_ID_PREQUAL_FORM_START_PAGE,
        SCREEN_ID_LISTING_CHOOSE_TOUR_TIME,
      ],
    });

    return editMode === 'field' && listingId
      ? `/admin/application/${userId}/${listingId}/${applicationId}`
      : !previousFormField && listingId
      ? `/application/${userId}/${listingId}/start`
      : !previousFormField && formType === APPLICATION_FORM_TYPE_RENTER_PREQUAL
      ? prequalListingId
        ? `/listing/${prequalListingId}`
        : lastPageVisited
        ? lastPageVisited.path
        : `/application/prequal-start${location.search}`
      : getListingApplicationFormUrl({
          userId: userId,
          listingId: listingId,
          householdId: householdId,
          formFieldId: (previousFormField && previousFormField.id) || null,
          prequalListingId: prequalListingId,
          formType: formType,
          requestedShowing: requestedShowing,
          saveExitRedirect,
          fieldGroup,
        });
  };

  render() {
    const {
      listing,
      currentUser,
      renterProfilesByUserId,
      householdRentersByUserId,
      householdApplicationsById,
      getRenterProfileApiState,
      getHouseholdRentersApiState,
      getHouseholdApplicationApiState,
      submitHouseholdApplicationApiState,
      location,
      dispatch,
      match: { params },
      screenIdHistory,
      pathHistory,
      householdListingsBy,
      isTransitioning,
    } = this.props;

    const { applicationFormFields } = this.state;

    const userId = parseInt(params.userId, 10);
    const listingId = parseInt(params.listingId, 10) || null;
    const applicationId = params.applicationId ? parseInt(params.applicationId, 10) : null;
    const locationSearch = queryString.parse(location.search);
    const {
      editMode,
      fieldId,
      formType,
      prequalListingId,
      householdId: householdIdStr,
      saveExitRedirect,
      fieldGroup,
    } = locationSearch;

    const householdId = householdIdStr ? parseInt(householdIdStr, 10) : null;

    if (isTransitioning) {
      return null;
    }

    if (!fieldId || !formType) {
      return null;
    }

    const householdApplication = applicationId
      ? householdApplicationsById[applicationId]
      : renterProfilesByUserId[userId];

    const householdListing = applicationId
      ? householdListingsBy[`${householdApplication.householdId}-${listingId}`]
      : null;

    const roommates =
      householdRentersByUserId && householdRentersByUserId[userId]
        ? householdRentersByUserId[userId].filter(({ id }) => id !== userId)
        : [];

    typeCheck('roommates::Array', roommates);

    const isLoading =
      getRenterProfileApiState.inProgress ||
      getHouseholdRentersApiState.inProgress ||
      getHouseholdApplicationApiState.inProgress ||
      !householdApplication;
    const isSubmitting = submitHouseholdApplicationApiState.inProgress;
    const apiError = getRenterProfileApiState.error || getHouseholdRentersApiState.error;
    const initialValues = createInitialValues({
      formFields: getApplicationFormFields({
        formValues: getHouseholdApplicationDataFields(householdApplication),
        roommates: roommates,
        formType: formType,
        uploadAs: userId,
        listing: listing,
      }),
      defaultValues: emptySsn(getHouseholdApplicationDataFields(householdApplication)),
    });
    const formFieldIndex = lodashFindIndex(applicationFormFields, (formField) => {
      return formField.id.toLowerCase() === fieldId.toLowerCase();
    });
    let buttonText = null;
    let buttonClassName = null;

    if (
      formType === APPLICATION_FORM_TYPE_RENTER_PREQUAL &&
      !prequalListingId &&
      applicationFormFields &&
      applicationFormFields.length - 1 === formFieldIndex && // Is last field in application
      !lodashIsEmpty(lodashPick(locationSearch, TOUR_REQUEST_SHOWING_FIELDS))
    ) {
      buttonText = 'request tour';
      buttonClassName = 'googleTagManager-requestTour';
    }

    debug('render', {
      initialValues,
      fieldId,
      applicationId,
      apiError,
      isLoading,
      formType,
      householdApplication,
      applicationFormFields,
    });

    return (
      <div className="page-rentalApplication">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validate={applicationFormFields ? createFormValidationFn(applicationFormFields) : null}
          render={(formikBag) => (
            <SrApplicationFormPage
              currentUser={currentUser}
              buttonText={buttonText}
              buttonClassName={buttonClassName}
              fieldId={fieldId}
              roommates={roommates}
              formikBag={formikBag}
              isLoading={isLoading}
              isSubmitting={isSubmitting}
              apiError={apiError}
              onFieldSubmit={this._onFieldSubmit}
              userId={userId}
              listingId={listingId}
              householdId={householdId}
              formType={formType}
              onFormFieldsReady={this._onFormFieldsReady}
              dispatch={dispatch}
              editMode={editMode}
              backLink={this._getBackLink()}
              hasBuyersAgent={householdListing && householdListing.hasBuyersAgent}
              prequalListingId={prequalListingId}
              application={householdApplication || {}}
              screenIdHistory={screenIdHistory}
              pathHistory={pathHistory}
              saveExitRedirect={saveExitRedirect}
              fieldGroup={fieldGroup}
              listing={listing}
            />
          )}
        />
      </div>
    );
  }
}

ApplicationFormPage.displayName = 'ApplicationFormPage';

export default ApplicationFormPage;
